import React, { useMemo } from "react";
import { Box, Button, Stack, Typography } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { useTranslation } from "react-i18next";

import RxGhgAccordion from "./RxGhgAccordion";
import { DEFAULT_UNIT } from "../constants";
import { numFromAny } from "../utils";

export default function ReceiverFactorsPanel({
  receiverRowsFiltered = [],
  receiverSelection = [],
  setReceiverSelection = () => {},

  // Used to persist edits back to the real rows
  setReceiverRows = () => {},

  receiverRowCount = 0,
  receiverSaving = false,

  addReceiverRow = () => {},
  deleteReceiverRow = () => {},
  saveReceiverRows = () => {},
}) {
  const { t } = useTranslation();

  const receiverColumns = useMemo(
    () => [
      { field: "row_id", headerName: t("ghg.receiverFactors.columns.index", "#"), width: 70 },

      { field: "name", headerName: t("ghg.receiverFactors.columns.material", "Material"), flex: 1, editable: false },

      {
        field: "emission_factor",
        headerName: t("ghg.receiverFactors.columns.emissionFactor", "Emission factor"),
        flex: 1,
        editable: true,
      },
      {
        field: "recycling_emissions",
        headerName: t("ghg.receiverFactors.columns.recyclingEmissions", "Recycling emissions"),
        flex: 1,
        editable: true,
      },

      {
        field: "energy_consumption_kwh",
        headerName: t("ghg.receiverFactors.columns.energyConsumption", "Energy consumption (kWh)"),
        flex: 1,
        editable: true,
        valueGetter: (arg1, arg2) => {
          const row = arg1?.row ? arg1.row : arg2;
          const v = numFromAny(row?.energy_consumption_kwh);
          return v == null ? null : v;
        },
        renderCell: (params) => {
          const v = numFromAny(params?.row?.energy_consumption_kwh);
          if (v == null) return "";
          return v.toFixed(3);
        },
      },

      {
        field: "waste_stage_qty",
        headerName: t("ghg.receiverFactors.columns.wasteStageQty", "Waste stage (qty)"),
        flex: 1,
        editable: true,
        valueGetter: (arg1, arg2) => {
          const row = arg1?.row ? arg1.row : arg2;
          const v = numFromAny(row?.waste_stage_qty);
          return v == null ? null : v;
        },
        renderCell: (params) => {
          const v = numFromAny(params?.row?.waste_stage_qty);
          if (v == null) return "";
          return v.toFixed(3);
        },
      },

      {
        field: "net_avoided_factor",
        headerName: t("ghg.receiverFactors.columns.netAvoided", "Net avoided ({{unit}})", {
          unit: DEFAULT_UNIT,
        }),
        flex: 1,
        sortable: false,
        filterable: false,
        editable: false,

        // Supports BOTH signatures:
        // - valueGetter(params)
        // - valueGetter(value, row)
        valueGetter: (arg1, arg2) => {
          const row = arg1?.row ? arg1.row : arg2;

          const ef = numFromAny(row?.emission_factor);
          const re = numFromAny(row?.recycling_emissions);
          if (ef == null || re == null) return null;
          return ef - re;
        },

        renderCell: (params) => {
          const row = params?.row;
          const ef = numFromAny(row?.emission_factor);
          const re = numFromAny(row?.recycling_emissions);
          if (ef == null || re == null) return "";
          return (ef - re).toFixed(6);
        },
      },
    ],
    [t]
  );

  return (
    <Box className="rxghg_block">
      <RxGhgAccordion
        id="rxghg_receiver"
        title={t("ghg.receiverFactors.title", "Receiver factors")}
        subtitle={t("ghg.receiverFactors.subtitle", "Edit material emission factors")}
        rightEl={
          <Box className="rxghg_accBadges">
            <span className="rxghg_badge">
              {t("ghg.common.rowsCount", "{{count}} rows", { count: receiverRowCount })}
            </span>
          </Box>
        }
      >
        <Box className="rxghg_gridWrap">
          <DataGrid
            rows={receiverRowsFiltered || []}
            columns={receiverColumns || []}
            getRowId={(r) => r.row_id}
            checkboxSelection
            disableRowSelectionOnClick
            onRowSelectionModelChange={(ids) => setReceiverSelection(ids)}
            processRowUpdate={(newRow) => {
              // Persist edits back to the source state
              setReceiverRows((prev) =>
                (prev || []).map((r) => (r.row_id === newRow.row_id ? newRow : r))
              );
              return newRow;
            }}
          />
        </Box>

        <Stack direction="row" spacing={1} className="rxghg_rowActions">
          <Button
            className="rxghg_btn"
            variant="contained"
            size="small"
            onClick={addReceiverRow}
          >
            {t("ghg.common.actions.add", "Add")}
          </Button>

          <Button
            className="rxghg_btn rxghg_btnGhost"
            variant="outlined"
            size="small"
            onClick={deleteReceiverRow}
            disabled={(receiverSelection || []).length === 0}
          >
            {t("ghg.common.actions.delete", "Delete")}
          </Button>

          <Box sx={{ flex: 1 }} />

          <Button
            className="rxghg_btn"
            variant="contained"
            size="small"
            onClick={saveReceiverRows}
            disabled={receiverSaving}
          >
            {receiverSaving
              ? t("ghg.common.actions.saving", "Saving…")
              : t("ghg.common.actions.save", "Save")}
          </Button>
        </Stack>

        <Typography
          variant="caption"
          sx={{ opacity: 0.7, mt: 1, display: "block" }}
        >
          {t(
            "ghg.receiverFactors.caption",
            "Net avoided factor = emission factor − recycling emissions."
          )}
        </Typography>
      </RxGhgAccordion>
    </Box>
  );
}